import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DiscussionError, listMyComments, type MyComment } from '@peruselab/api-client/discussions';
import { relativeTime } from '../../components/panels/MyDrawingsFeed';
import { publicDrawingPath } from '../public/publicAccess';

type State =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly comments: readonly MyComment[] }
  | { readonly status: 'error'; readonly kind: DiscussionError['kind'] };

function artifactPath(comment: MyComment): string {
  return comment.artifactKind === 'molecule'
    ? `/objects/molecule/${encodeURIComponent(comment.artifactId)}`
    : publicDrawingPath(comment.artifactId);
}

/**
 * #886 Q5 / ADR 0016 D14: your own comments, newest first. When an artifact is no longer yours to
 * read (made private, archived away from you), your words stay here -- the artifact doesn't.
 */
export function MyCommentsPage() {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    listMyComments()
      .then((comments) => setState({ status: 'ready', comments }))
      .catch((error: unknown) =>
        setState({ status: 'error', kind: error instanceof DiscussionError ? error.kind : 'unknown' })
      );
  }, []);

  return (
    <main className="my-comments">
      <h1>{t('discussions.myComments.heading')}</h1>
      {state.status === 'loading' ? <p role="status">{t('common.loading')}</p> : null}
      {state.status === 'error' ? (
        <p className="import-error" role="alert">
          {t(`discussions.errors.${state.kind}`)}
        </p>
      ) : null}
      {state.status === 'ready' && state.comments.length === 0 ? <p>{t('discussions.myComments.empty')}</p> : null}
      {state.status === 'ready' && state.comments.length > 0 ? (
        <ol className="my-comments-list">
          {state.comments.map((comment) => (
            <li key={comment.id} className="my-comment" data-testid="my-comment">
              <div className="comment-header">
                {comment.artifactAvailable ? (
                  <Link to={artifactPath(comment)} className="my-comment-artifact">
                    {comment.artifactTitle ||
                      (comment.artifactKind === 'molecule'
                        ? t('discussions.myComments.molecule')
                        : t('discussions.myComments.untitledDrawing'))}
                  </Link>
                ) : (
                  <span className="comment-meta">{t('discussions.myComments.unavailable')}</span>
                )}
                <span className="comment-meta">
                  {comment.reply ? `${t('discussions.myComments.reply')} · ` : ''}
                  {relativeTime(comment.createdAt, i18n.language)}
                  {comment.editedAt ? ` · ${t('discussions.edited')}` : ''}
                </span>
                {comment.hidden ? <span className="comment-badge">{t('discussions.hiddenBadge')}</span> : null}
              </div>
              <p className="comment-body" dir="auto">
                {comment.body}
              </p>
            </li>
          ))}
        </ol>
      ) : null}
    </main>
  );
}
